import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Edit, Trash2, X } from 'lucide-react';

// Event type definition
/**
 * @typedef {Object} Event
 * @property {number} id - The unique identifier for the event
 * @property {string} title - The title of the event
 * @property {string} dueDate - The due date of the event in YYYY-MM-DD format
 * @property {string} time - The time of the event in HH:mm format
 */

const TodoList = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  // Load tasks from localStorage on component mount
  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    } else {
      // Initialize with default tasks if localStorage is empty
      const defaultTasks = [
        { id: 1, title: "Meeting with Asfar", dueDate: "2025-06-03", time: "10:00" },
        { id: 2, title: "Schedule team meeting", dueDate: "2025-06-06", time: "10:00" },
        { id: 3, title: "Review client feedback", dueDate: "2025-06-08", time: "15:30" },
        { id: 4, title: "Update documentation", dueDate: "2025-06-12", time: "09:00" },
        { id: 5, title: "Team brainstorming session", dueDate: "2025-06-03", time: "11:00" },
        { id: 6, title: "Submit quarterly report", dueDate: "2025-06-03", time: "13:00" },
        { id: 7, title: "Client meeting", dueDate: "2025-06-10", time: "14:30" },
        { id: 8, title: "Plan marketing campaign", dueDate: "2025-06-15", time: "16:00" },
        { id: 9, title: "Attend workshop", dueDate: "2025-06-20", time: "10:00" },
        { id: 10, title: "Review budget", dueDate: "2025-06-25", time: "12:00" },
      ];
      setTasks(defaultTasks);
      localStorage.setItem('tasks', JSON.stringify(defaultTasks));
    }
  }, []);

  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    if (tasks.length > 0) {
      localStorage.setItem('tasks', JSON.stringify(tasks));
    }
  }, [tasks]);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    const totalSlots = Math.ceil((days.length) / 7) * 7;
    for (let i = days.length; i < totalSlots; i++) {
      days.push(null);
    }

    return days;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (day) => {
    if (!day) return;
    const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    selected.setHours(0, 0, 0, 0);
    setSelectedDate(selected);
    setShowAddEventForm(false);
    setEditingEvent(null);
    setCurrentEventIndex(0);
  };

  const hasEvent = (day) => {
    if (!day) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.some(task => task.dueDate === dateStr);
  };

  const isSelectedDate = (day) => {
    if (!day) return false;
    return (
      day === selectedDate.getDate() && 
      selectedDate.getMonth() === currentDate.getMonth() &&
      selectedDate.getFullYear() === currentDate.getFullYear()
    );
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}${minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : ''}${period}`;
  };

  const handleAddEvent = (newEvent) => {
    const newEventObj = {
      id: tasks.length > 0 ? Math.max(...tasks.map(t => t.id)) + 1 : 1,
      ...newEvent
    };

    setTasks([...tasks, newEventObj]);
    setShowAddEventForm(false);
  };

  const handleUpdateEvent = (updatedEvent) => {
    setTasks(
      tasks.map(task =>
        task.id === updatedEvent.id ? updatedEvent : task
      )
    );
    setEditingEvent(null);
  };

  const handleDeleteClick = (eventId) => {
    setEventToDelete(eventId);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    if (eventToDelete !== null) {
      setTasks(tasks.filter(task => task.id !== eventToDelete));
      setShowDeleteConfirmation(false);
      setEventToDelete(null);
    }
  };

  const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  const dateEvents = tasks.filter(task => task.dueDate === selectedDateStr);

  const nextEvent = () => {
    if (currentEventIndex < dateEvents.length - 1) {
      setCurrentEventIndex(currentEventIndex + 1);
    }
  };

  const prevEvent = () => {
    if (currentEventIndex > 0) {
      setCurrentEventIndex(currentEventIndex - 1);
    }
  };

  return (
    <div className="bg-white shadow-md p-6 min-h-full max-w-full mx-auto">
      {/* Calendar View */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
          <div className="flex space-x-2">
            <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
              <ChevronLeft className="h-5 w-5 text-gray-500" />
            </button>
            <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
              <ChevronRight className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-sm">
          {daysOfWeek.map(day => (
            <div key={day} className="font-medium text-gray-500 py-1">{day}</div>
          ))}
          {generateCalendar().map((day, index) => {
            const isSelected = isSelectedDate(day);
            const hasEventOnDay = hasEvent(day);
            const isPast = day ? isPastDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day)) : false;
            
            return (
              <button
                key={index}
                onClick={() => handleDateSelect(day)}
                className={`p-2 rounded-lg relative transition-all duration-200 ${
                  day
                    ? isSelected
                      ? 'bg-blue-500 text-white transform scale-105 shadow-md'
                      : hasEventOnDay
                      ? isPast
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : isPast
                      ? 'text-gray-400 hover:bg-gray-100'
                      : 'text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-default'
                }`}
                disabled={!day}
              >
                {day || ''}
                {hasEventOnDay && (
                  <span className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1 rounded-full ${
                    isPast ? 'bg-gray-400' : 'bg-blue-500'
                  }`}></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Add Event Form */}
      {showAddEventForm ? (
        <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200 animate-fadeIn">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-blue-700">
              Add Event for {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </h4>
            <button onClick={() => setShowAddEventForm(false)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {isPastDate(selectedDate) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 text-amber-700 text-sm">
              Cannot add events to past dates. Please select a current or future date.
            </div>
          ) : (
            <form onSubmit={(e) => {
              e.preventDefault();
              const form = e.target;
              const formData = new FormData(form);
              handleAddEvent({
                title: formData.get('title'),
                dueDate: formatDateForInput(selectedDate),
                time: formData.get('time')
              });
            }} className="space-y-3">
              <div>
                <label htmlFor="event-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title
                </label>
                <input
                  id="event-title"
                  name="title"
                  type="text"
                  placeholder="Enter event title"
                  className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-200 focus:ring-blue-300"
                  autoFocus
                  required
                />
              </div>
              
              <div>
                <label htmlFor="event-time" className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <div className="relative">
                  <input
                    id="event-time"
                    name="time"
                    type="time"
                    defaultValue="10:00"
                    className="w-full p-2 pl-8 text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-200 focus:ring-blue-300"
                  />
                  <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddEventForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Add Event
                </button>
              </div>
            </form>
          )}
        </div>
      ) : editingEvent ? (
        <div className="bg-amber-50 p-4 rounded-lg mb-6 border border-amber-200 animate-fadeIn">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-amber-700">
              Edit Event
            </h4>
            <button onClick={() => setEditingEvent(null)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const form = e.target;
            const formData = new FormData(form);
            handleUpdateEvent({
              ...editingEvent,
              title: formData.get('title'),
              time: formData.get('time')
            });
          }} className="space-y-3">
            <div>
              <label htmlFor="edit-event-title" className="block text-sm font-medium text-gray-700 mb-1">
                Event Title
              </label>
              <input
                id="edit-event-title"
                name="title"
                type="text"
                defaultValue={editingEvent.title}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-200 focus:ring-amber-300"
                autoFocus
                required
              />
            </div>
            
            <div>
              <label htmlFor="edit-event-time" className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <div className="relative">
                <input
                  id="edit-event-time"
                  name="time"
                  type="time"
                  defaultValue={editingEvent.time}
                  className="w-full p-2 pl-8 text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-200 focus:ring-amber-300"
                />
                <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
            
            <div className="pt-2 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setEditingEvent(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Events on {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          
          {dateEvents.length === 0 ? (
            <div className="text-gray-500 mb-4">No events for this date.</div>
          ) : (
            <div className="mb-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className="w-16 h-16 bg-blue-500 text-white flex flex-col items-center justify-center rounded-md">
                      <div className="text-xs font-medium">{selectedDate.toLocaleString('en-US', { month: 'short' }).toUpperCase()}</div>
                      <div className="text-2xl font-bold">{selectedDate.getDate()}</div>
                    </div>
                    
                    <div className="ml-4 flex-1">
                      <h4 className="font-medium text-gray-800">{dateEvents[currentEventIndex].title}</h4>
                      <p className="text-sm text-gray-500">{formatTime(dateEvents[currentEventIndex].time)}</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setEditingEvent(dateEvents[currentEventIndex])}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-colors text-gray-500 hover:bg-blue-100 hover:text-blue-600"
                      title="Edit event"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(dateEvents[currentEventIndex].id)}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-colors text-gray-500 hover:bg-red-100 hover:text-red-600"
                      title="Delete event"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {dateEvents.length > 1 && (
                  <div className="mt-3 flex items-center justify-between">
                    <button 
                      onClick={prevEvent}
                      disabled={currentEventIndex === 0}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        currentEventIndex === 0 
                          ? 'text-gray-300 cursor-not-allowed' 
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    <div className="text-sm text-gray-500 font-medium">
                      {currentEventIndex + 1} / {dateEvents.length}
                    </div>
                    
                    <button 
                      onClick={nextEvent} 
                      disabled={currentEventIndex >= dateEvents.length - 1}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        currentEventIndex >= dateEvents.length - 1 
                          ? 'text-gray-300 cursor-not-allowed' 
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <button 
            onClick={() => setShowAddEventForm(true)}
            disabled={isPastDate(selectedDate)}
            className={`w-full py-3 font-medium rounded-lg transition-colors text-center ${
              isPastDate(selectedDate)
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
            title={isPastDate(selectedDate) ? "Cannot add events to past dates" : "Add a new event"}
          >
            {isPastDate(selectedDate) ? "Cannot add events to past dates" : "Add Event"}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Delete Event</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this event? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setEventToDelete(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoList;